import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

const AddModuleHere = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    moduleName: '',
    moduleCode: '',
    semester: '',
   
   // Add this to store actual staff UUID
    credits: 3,
    description: '',
    photo: null,
    status: 'Active'
  });

  // Staff list for module leader dropdown
  const [staffList, setStaffList] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [showStaffDropdown, setShowStaffDropdown] = useState(false);
  const [staffSearchQuery, setStaffSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Load staff data on component mount
  useEffect(() => {
    const loadStaff = async () => {
      try {
        setLoading(true);
        
        // Fetch real staff data from Supabase
        // First get admin's university_id to filter staff
        let universityId = currentUser?.university_id;
        
        if (!universityId && currentUser?.id) {
          // If university_id not in user context, get from admins table
          const { data: adminData, error: adminError } = await supabase
            .from('admins')
            .select('university_id')
            .eq('user_id', currentUser.id)
            .single();
          
          if (!adminError && adminData) {
            universityId = adminData.university_id;
          }
        }

        if (!universityId) {
          console.error('Unable to determine university_id for staff filtering');
          // Fall back to mock data if can't determine university
          const mockStaff = [
            { id: '1', name: 'Dr. John Smith', staff_type: 'lecturer' },
            { id: '2', name: 'Prof. Sarah Johnson', staff_type: 'professor' },
            { id: '3', name: 'Dr. Michael Brown', staff_type: 'lecturer' },
            { id: '4', name: 'Prof. Emily Davis', staff_type: 'professor' },
            { id: '5', name: 'Dr. David Wilson', staff_type: 'lecturer' }
          ];
          setStaffList(mockStaff);
          setFilteredStaff(mockStaff);
          return;
        }

        // Query staff from same university
        const { data: staffData, error: staffError } = await supabase
          .from('staff')
          .select('id, name, staff_type, active')
          .eq('university_id', universityId)
          .eq('active', true)
          .order('name', { ascending: true });

        if (staffError) {
          console.error('Error loading staff:', staffError);
          throw staffError;
        }

        console.log(`Loaded ${staffData?.length || 0} staff members for university ${universityId}`);
        setStaffList(staffData || []);
        setFilteredStaff(staffData || []);
        
      } catch (error) {
        console.error('Error loading staff:', error);
        // Fall back to mock data on error
        const mockStaff = [
          { id: '1', name: 'Dr. John Smith', staff_type: 'lecturer' },
          { id: '2', name: 'Prof. Sarah Johnson', staff_type: 'professor' },
          { id: '3', name: 'Dr. Michael Brown', staff_type: 'lecturer' },
          { id: '4', name: 'Prof. Emily Davis', staff_type: 'professor' },
          { id: '5', name: 'Dr. David Wilson', staff_type: 'lecturer' }
        ];
        setStaffList(mockStaff);
        setFilteredStaff(mockStaff);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadStaff();
    }
  }, [currentUser]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setFormData(prev => ({
      ...prev,
      photo: file
    }));
  };

  // Upload photo to Supabase storage
  const uploadPhoto = async (file) => {
    if (!file) return null;

    try {
      setUploading(true);
      
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `module-${Date.now()}.${fileExt}`;
      
      // Upload to Supabase storage
      const { data, error } = await supabase.storage
        .from('module-images')
        .upload(fileName, file);

      if (error) {
        console.error('Upload error:', error);
        throw error;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('module-images')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  // Handle staff search and selection
  const handleStaffSearch = (e) => {
    const query = e.target.value;
    setStaffSearchQuery(query);
    
    if (query.trim() === '') {
      setFilteredStaff(staffList);
    } else {
      const filtered = staffList.filter(staff => 
        staff.name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredStaff(filtered);
    }
    setShowStaffDropdown(true);
  };

  const handleStaffSelect = (staff) => {
    console.log('Selected staff:', staff); // Debug log
    setFormData(prev => ({
      ...prev,
     
      
    }));
    setStaffSearchQuery(staff.name);
    setShowStaffDropdown(false);
    
    // Log updated form data for debugging
    console.log('Updated formData:', {
     
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the staff dropdown area
      const dropdownElement = event.target.closest('[data-staff-dropdown]');
      const inputElement = event.target.closest('[data-staff-input]');
      
      if (!dropdownElement && !inputElement && showStaffDropdown) {
        setShowStaffDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStaffDropdown]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Debug: Log current form data
    console.log('Current formData on submit:', formData);
    
    if (!formData.moduleName || !formData.moduleCode) {
      alert('Module Name and Module Code are required');
      return;
    }

   

    if (!currentUser) {
      alert('Please log in to add modules');
      return;
    }

    try {
      setLoading(true);

      // Get admin's university_id
      let universityId = currentUser?.university_id;
      
      if (!universityId && currentUser?.id) {
        // If university_id not in user context, get from admins table
        const { data: adminData, error: adminError } = await supabase
          .from('admins')
          .select('university_id')
          .eq('user_id', currentUser.id)
          .single();
        
        if (adminError || !adminData) {
          throw new Error('Unable to determine your university. Please contact support.');
        }
        
        universityId = adminData.university_id;
      }

      if (!universityId) {
        throw new Error('University ID not found. Please contact support.');
      }

      // Upload photo if provided
      let photoUrl = null;
      if (formData.photo) {
        try {
          photoUrl = await uploadPhoto(formData.photo);
        } catch (uploadError) {
          console.error('Photo upload failed:', uploadError);
          // Continue without photo rather than failing the entire submission
          alert('Photo upload failed, but module will be created without photo');
        }
      }

      // Prepare module data for Supabase modules table
      const moduleData = {
        module_name: formData.moduleName,        // "Module Name" → module_name
        module_code: formData.moduleCode,        // "Module Code" → module_code  
        semester: parseInt(formData.semester),   // "Semester" → semester
         // "Module Leader" → module_leader_id (UUID)
        credits: parseInt(formData.credits),     // "Credits" → credits
        description: formData.description || null, // "Description" → description
        photo_url: photoUrl,                     // "Photo" → photo_url
        active: formData.status === 'Active',    // "Status" → active (boolean)
        university_id: universityId,             // Auto-filled admin's university_id
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('Submitting module data to Supabase modules table:', moduleData);

      // Insert into Supabase modules table
      const { error } = await supabase
        .from('modules')
        .insert([moduleData])
        .select()
        .single();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Module created successfully!');
      alert('Module added successfully!');
      navigate('/add-module');

    } catch (error) {
      console.error('Error adding module:', error);
      let errorMessage = 'Error adding module: ';
      
      if (error.message) {
        errorMessage += error.message;
      } else if (error.details) {
        errorMessage += error.details;
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
      <Sidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
        <Header />
        
        {!currentUser ? (
          <div style={{margin: '40px', textAlign: 'center', padding: '40px'}}>
            <h2 style={{color: '#F04915', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>
              Please Log In
            </h2>
            <p style={{fontSize: 16, fontFamily: 'Outfit', color: '#77838F'}}>
              You need to be logged in as an admin to add modules.
            </p>
            <Link to="/login">
              <button style={{
                padding: '12px 24px',
                background: '#F04915',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontFamily: 'Outfit',
                fontWeight: '500',
                cursor: 'pointer',
                marginTop: '20px'
              }}>
                Go to Login
              </button>
            </Link>
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          <div style={{margin: '20px 40px', position: 'relative'}}>
            <div style={{background: '#F04915', borderTopLeftRadius: 15, borderTopRightRadius: 15, padding: '20px', color: 'white'}}>
              <h2 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>Add New Module</h2>
            </div>

            <div style={{background: '#FFFBF5', padding: '40px'}}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                {/* Left Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Module Name <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="moduleName"
                      type="text"
                      placeholder="Machine Learning"
                      value={formData.moduleName}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Module Code <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="moduleCode"
                      type="text"
                      placeholder="CS401"
                      value={formData.moduleCode}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Semester <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="semester"
                      value={formData.semester}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Semester</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                    </select>
                  </div>

                  
                </div>

                {/* Right Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Credits
                    </label>
                    <input
                      name="credits"
                      type="number"
                      min="1"
                      max="6"
                      value={formData.credits}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Status <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Photo
                    </label>
                    <div style={{width: '100%', height: 120, border: '1px dashed #77838F', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'}}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        disabled={uploading}
                        style={{opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: uploading ? 'not-allowed' : 'pointer'}}
                      />
                      <div style={{color: '#77838F', fontSize: 16, fontFamily: 'Outfit', fontWeight: '400', textAlign: 'center'}}>
                        {uploading ? 'Uploading...' : 
                         formData.photo ? `Selected: ${formData.photo.name}` : 
                         'Click to upload photo'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description - Full Width */}
                <div style={{gridColumn: '1 / -1', marginTop: '20px'}}>
                  <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                    Description
                  </label>
                  <textarea
                    name="description"
                    placeholder="Enter module description..."
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="4"
                    style={{
                      width: '100%', 
                      padding: '15px', 
                      borderRadius: 8, 
                      border: '1px solid #77838F', 
                      fontSize: 16, 
                      fontFamily: 'Outfit',
                      resize: 'vertical',
                      minHeight: '100px'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{margin: '40px', display: 'flex', justifyContent: 'space-between'}}>
            <Link to="/add-module">
              <button 
                type="button" 
                style={{
                  width: 269, 
                  padding: '11px 20px', 
                  background: '#6C757D', 
                  border: 'none', 
                  borderRadius: 8, 
                  color: 'white', 
                  fontSize: 16, 
                  fontFamily: 'Outfit', 
                  fontWeight: '500', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#5A6268';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#6C757D';
                }}
              >
                Back
              </button>
            </Link>
            <button 
              type="submit" 
              disabled={loading || uploading}
              style={{
                width: 269, 
                padding: '11px 20px', 
                background: loading || uploading ? '#ccc' : '#F04915', 
                border: 'none', 
                borderRadius: 8, 
                color: 'white', 
                fontSize: 16, 
                fontFamily: 'Outfit', 
                fontWeight: '500', 
                cursor: loading || uploading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                if (!loading && !uploading) {
                  e.target.style.background = '#D63A0F';
                }
              }}
              onMouseOut={(e) => {
                if (!loading && !uploading) {
                  e.target.style.background = '#F04915';
                }
              }}
            >
              {loading ? 'Adding Module...' : 'Submit'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default AddModuleHere;
